import { GP_TYPE } from '@lmc/ts-common'
import { getCleanRoute } from 'hooks/useRouteMatch'
import { useRouter } from 'hooks/useRouter'
import useFeatureFlags from 'hooks/useFeatureFlags'

const profileRoutes = [
  '/residents/[id]/profile',
  '/residents/[id]/needs',
  '/residents/[id]/important-people',
  '/residents/[id]/about-me',
  '/residents/[id]/future-plans',
  '/residents/[id]/medications',
  '/residents/[id]/care-package-and-patterns'
]

const chartsRoutes = [
  '/residents/[id]/charts',
  '/residents/[id]/risk-assessment-scores'
]

const carePlanRoutes = [
  '/residents/[id]/care-plans',
  '/residents/[id]/initial-assessments',
  '/residents/[id]/risk-assessments',
  '/residents/[id]/goals'
]

const emarRoutes = ['/residents/[id]/emar', '/residents/[id]/emar/stock']

const gpConnetRoutes = [
  `/residents/[id]/gp-connect/${GP_TYPE.ADM}`,
  `/residents/[id]/gp-connect/${GP_TYPE.ALL}`,
  `/residents/[id]/gp-connect/${GP_TYPE.CLI}`,
  `/residents/[id]/gp-connect/${GP_TYPE.ENC}`,
  `/residents/[id]/gp-connect/${GP_TYPE.IMM}`,
  `/residents/[id]/gp-connect/${GP_TYPE.MED}`,
  `/residents/[id]/gp-connect/${GP_TYPE.OBS}`,
  `/residents/[id]/gp-connect/${GP_TYPE.PRB}`,
  `/residents/[id]/gp-connect/${GP_TYPE.REF}`,
  `/residents/[id]/gp-connect/${GP_TYPE.SUM}`
]

const getDocumentationRoutes = (
  communicationRecordsEnabled: boolean,
  proceduresEnabled: boolean = false,
  referralsEnabled: boolean = false,
  serviceRecordsEnabled: boolean = false,
  transferRecordsEnabled: boolean = false,
  deviceRecordsEnabled: boolean = false
) => {
  const routes = ['/residents/[id]/documents']

  if (communicationRecordsEnabled) {
    routes.push('/residents/[id]/communication-records')
  }

  if (proceduresEnabled) {
    routes.push('/residents/[id]/procedures')
  }

  if (referralsEnabled) {
    routes.push('/residents/[id]/referrals')
  }

  if (serviceRecordsEnabled) {
    routes.push('/residents/[id]/service-records')
  }

  if (transferRecordsEnabled) {
    routes.push('/residents/[id]/transfer-records')
  }

  if (deviceRecordsEnabled) {
    routes.push('/residents/[id]/device-records')
  }

  return routes
}

export const useResidentsSidebarNavigation = () => {
  const router = useRouter()

  const { location } = router

  const flags = useFeatureFlags({
    flagNames: [
      'communication_records',
      'procedures',
      'referrals',
      'service_records',
      'transfer_records',
      'device_records'
    ]
  })?.data

  const communicationRecordsEnabled = flags?.communication_records?.enabled
  const proceduresEnabled = flags?.procedures?.enabled
  const referralsEnabled = flags?.referrals?.enabled
  const serviceRecordsEnabled = flags?.service_records?.enabled
  const transferRecordsEnabled = flags?.transfer_records?.enabled
  const deviceRecordsEnabled = flags?.device_records?.enabled

  const documentationRoutes = getDocumentationRoutes(
    communicationRecordsEnabled || false,
    proceduresEnabled,
    referralsEnabled,
    serviceRecordsEnabled,
    transferRecordsEnabled,
    deviceRecordsEnabled
  )

  const cleanRoute = getCleanRoute(location.pathname, router.params)

  const getLastUrlKey = () => router.location.pathname.split('/').slice(-1)[0]

  const getDefaultOpenKey = () => {
    if (profileRoutes.includes(cleanRoute)) {
      return 'profile-route'
    }

    if (chartsRoutes.includes(cleanRoute)) {
      return 'charts-route'
    }

    if (carePlanRoutes.includes(cleanRoute)) {
      return 'care-plans-route'
    }

    if (emarRoutes.includes(cleanRoute)) {
      return 'emar-route'
    }

    if (gpConnetRoutes.includes(cleanRoute)) {
      return 'gp-connect-route'
    }

    if (documentationRoutes.includes(cleanRoute)) {
      return 'documentation-route'
    }

    return ''
  }

  const defaultOpenKey = getDefaultOpenKey()

  const lastUrlKey = getLastUrlKey()

  const getSelectedKey = () => {
    switch (defaultOpenKey) {
      case 'profile-route':
      case 'care-plans-route':
      case 'charts-route':
      case 'documentation-route':
        return lastUrlKey
      case 'emar-route':
        if (lastUrlKey === 'stock') {
          return 'emar/stock'
        }
        return lastUrlKey
      case 'gp-connect-route':
        return `gp-connect/${lastUrlKey}`
      default:
        return lastUrlKey
    }
  }

  const defaultSelectedKey = getSelectedKey()

  return { defaultOpenKey, defaultSelectedKey }
}
